import { faExclamationTriangle, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { faCircleXmark } from '@fortawesome/pro-duotone-svg-icons';

import { App } from 'components/root/app';
import { BrowserRouter } from 'react-router-dom';
import { DeviceProvider } from '@hqo/react-components-library/dist/device';
import { HqoThemeProvider } from '@hqo/react-components-library/dist/hqo-theme-provider';
import { IntlProvider } from 'components/root/intl-provider';
import React from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { RootStyles } from './root.styles';
import { useStore } from 'store/useStore.hook';
import { ErrorBoundary, init as initSentry, browserTracingIntegration } from '@sentry/react';
import { ErrorPage } from 'pages/error';
import { MiniappSdkProvider } from 'components/miniapp-sdk-provider';
import { HostInfoProvider } from 'components/host-info-provider';
import { ActivityPaneProvider } from 'components/activity-pane-provider';
import { library } from '@fortawesome/fontawesome-svg-core';

library.add(faExclamationTriangle, faSearch, faCircleXmark);
const FAKE_SENTRY_DSN = 'FAKE_SENTRY_DSN';
const isSentryEnabled = process.env.REACT_APP_SENTRY_DSN !== FAKE_SENTRY_DSN;

if (isSentryEnabled) {
  initSentry({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    release: process.env.REACT_APP_VERSION,
    autoSessionTracking: true,
    environment: process.env.NODE_ENV,
    integrations: [browserTracingIntegration()],
    tracesSampleRate: 1.0,
    normalizeDepth: 10,
  });
}

const ReduxProviderWithStore: React.FC = (): JSX.Element => {
  const store = useStore();

  return (
    <ReduxProvider store={store}>
      <MiniappSdkProvider>
        <HostInfoProvider>
          <HqoThemeProvider>
            <IntlProvider>
              <ErrorBoundary fallback={<ErrorPage />}>
                <DeviceProvider>
                  <RootStyles />
                  <ActivityPaneProvider>
                    <App />
                  </ActivityPaneProvider>
                </DeviceProvider>
              </ErrorBoundary>
            </IntlProvider>
          </HqoThemeProvider>
        </HostInfoProvider>
      </MiniappSdkProvider>
    </ReduxProvider>
  );
};

export const Root: React.FC = (): JSX.Element => (
  <LDProvider clientSideID={process.env.REACT_APP_TENANT_LAUNCH_DARKLY_CLIENT_ID || ''}>
    <BrowserRouter>
      <ReduxProviderWithStore />
    </BrowserRouter>
  </LDProvider>
);
